import * as BABYLON from '@babylonjs/core'
import { DisposerBag } from '../../sceneKit/disposerBag'
import { buildGradientSkyDome } from '../../sceneKit/skyDome'

/**
 * Builds the whole "walkable museum hall" scene: dusk plaza, glass doors,
 * PBR/IBL lighting, a mirror floor, framed prints, and a bloom/ACES/SSAO2
 * post pipeline. Framework-agnostic — takes a live `scene`/`canvas` and a
 * photo list, builds everything, and hands back a small imperative API
 * (`playIntro`/`stepOutside`/`dispose`) for the React screen to drive.
 *
 * Fixed at 7 print slots (6 side-wall + 1 end-wall finale) — real photos are
 * cycled to fill them when a gallery has fewer than 7. Not a general layout
 * engine; Studio's freeform hang tool still owns that.
 */

export interface HallPrintPhoto {
  url: string
  /** width / height. Falls back to 4/3 when unknown (see PresentRealisticScreen). */
  ratio: number
  name?: string
}

export interface RealisticHallCallbacks {
  /** Fires once the scene + its texture set have loaded far enough to hide the loading veil. */
  onReady?: () => void
  /** Fires whenever explore/HUD chrome should show or hide (entering/leaving the hall). */
  onExploreChange?: (inside: boolean) => void
}

export interface RealisticHallApi {
  camera: BABYLON.UniversalCamera
  /** Cinematic door-open walk-in. `skip = true` snaps straight inside (used for deep-links / reduced motion). */
  playIntro: (skip?: boolean) => void
  /** Reverse of playIntro — closes the doors and returns to the plaza view. */
  stepOutside: () => void
  isInside: () => boolean
  dispose: () => void
}

const DOOR_W = 3.2
const DOOR_H = 3.35
const FACADE_W = 15
const FACADE_H = 6.2
const WALL_T = 0.6
const W_IN = 12
const H_IN = 6.2
const D_IN = 25

function box(scene: BABYLON.Scene, name: string, w: number, h: number, d: number, x: number, y: number, z: number, mat: BABYLON.Material) {
  const m = BABYLON.MeshBuilder.CreateBox(name, { width: w, height: h, depth: d }, scene)
  m.position.set(x, y, z)
  m.material = mat
  return m
}

function wallPlane(scene: BABYLON.Scene, name: string, w: number, h: number, x: number, y: number, z: number, rotY: number, mat: BABYLON.Material) {
  const p = BABYLON.MeshBuilder.CreatePlane(name, { width: w, height: h }, scene)
  p.position.set(x, y, z)
  p.rotation.y = rotY
  p.material = mat
  p.receiveShadows = true
  return p
}

export function buildRealisticHall(
  scene: BABYLON.Scene,
  engine: BABYLON.Engine,
  canvas: HTMLCanvasElement,
  photos: HallPrintPhoto[],
  callbacks: RealisticHallCallbacks = {},
): RealisticHallApi {
  const disposers: (() => void)[] = []
  const track = <T extends { dispose: () => void }>(thing: T): T => {
    disposers.push(() => thing.dispose())
    return thing
  }

  scene.clearColor = BABYLON.Color4.FromHexString('#0a0910ff')

  /* ---------- environment / IBL + art-directed dusk sky dome ---------- */
  const envTex = BABYLON.CubeTexture.CreateFromPrefilteredData('https://assets.babylonjs.com/environments/environmentSpecular.env', scene)
  scene.environmentTexture = envTex
  scene.environmentIntensity = 0.85
  disposers.push(() => envTex.dispose())

  // Shares its gradient-dome recipe with SpacesStudio/Mountain Lot via sceneKit; this hall
  // additionally kills depth-write so the dusk sky never z-fights behind the glass doors.
  const skyDomeBag: DisposerBag = { track, defer: (fn) => disposers.push(fn), disposeAll: () => {} }
  const skyDome = buildGradientSkyDome(
    scene,
    skyDomeBag,
    [
      [0, '#221c3a'],
      [0.3, '#3c2f52'],
      [0.55, '#7a4a52'],
      [0.78, '#c9754a'],
      [0.92, '#f0ab68'],
      [1, '#ffe0a0'],
    ],
    { diameter: 700 },
  )
  ;(skyDome.material as BABYLON.StandardMaterial).disableDepthWrite = true

  scene.fogMode = BABYLON.Scene.FOGMODE_EXP2
  scene.fogDensity = 0.0034
  scene.fogColor = BABYLON.Color3.FromHexString('#2a2030')

  /* ---------- sun + fill ---------- */
  const sun = track(new BABYLON.DirectionalLight('sun', new BABYLON.Vector3(0.55, -0.5, 0.66), scene))
  sun.position = new BABYLON.Vector3(-16, 15, -26)
  sun.intensity = 1.7
  sun.diffuse = BABYLON.Color3.FromHexString('#ffcf9a')
  const sunShadows = track(new BABYLON.ShadowGenerator(1024, sun))
  sunShadows.useBlurExponentialShadowMap = true
  sunShadows.blurKernel = 20
  sunShadows.darkness = 0.25

  const duskFill = track(new BABYLON.HemisphericLight('duskFill', new BABYLON.Vector3(0, 1, 0), scene))
  duskFill.intensity = 0.55
  duskFill.diffuse = BABYLON.Color3.FromHexString('#e9c9c0')
  duskFill.groundColor = BABYLON.Color3.FromHexString('#241c2c')

  /* ---------- exterior: plaza, facade, portico, steps, sign ---------- */
  const plazaMat = track(new BABYLON.PBRMaterial('plazaMat', scene))
  plazaMat.albedoColor = BABYLON.Color3.FromHexString('#5f574c')
  plazaMat.metallic = 0
  plazaMat.roughness = 0.75
  plazaMat.environmentIntensity = 0.45
  const plaza = track(BABYLON.MeshBuilder.CreateGround('plaza', { width: 42, height: 40 }, scene))
  plaza.position.z = -20
  plaza.material = plazaMat
  plaza.receiveShadows = true

  const facadeMat = track(new BABYLON.PBRMaterial('facadeMat', scene))
  facadeMat.albedoColor = BABYLON.Color3.FromHexString('#a29a89')
  facadeMat.metallic = 0
  facadeMat.roughness = 0.82
  facadeMat.environmentIntensity = 0.55

  const sideW = (FACADE_W - DOOR_W) / 2
  const facadeCasters: BABYLON.Mesh[] = []
  facadeCasters.push(track(box(scene, 'facadeL', sideW, FACADE_H, WALL_T, -(DOOR_W / 2 + sideW / 2), FACADE_H / 2, -WALL_T / 2, facadeMat)))
  facadeCasters.push(track(box(scene, 'facadeR', sideW, FACADE_H, WALL_T, DOOR_W / 2 + sideW / 2, FACADE_H / 2, -WALL_T / 2, facadeMat)))
  facadeCasters.push(track(box(scene, 'facadeTop', DOOR_W + 0.3, FACADE_H - DOOR_H, WALL_T, 0, DOOR_H + (FACADE_H - DOOR_H) / 2, -WALL_T / 2, facadeMat)))
  facadeCasters.push(track(box(scene, 'portico', FACADE_W + 1.6, 0.34, 3.2, 0, FACADE_H + 0.17, -2, facadeMat)));
  [-1, 1].forEach((s) => {
    facadeCasters.push(track(box(scene, 'col' + s, 0.34, FACADE_H, 0.34, s * (DOOR_W / 2 + 0.55), FACADE_H / 2, -3.3, facadeMat)))
  })

  const stepMat = track(new BABYLON.PBRMaterial('stepMat', scene))
  stepMat.albedoColor = BABYLON.Color3.FromHexString('#8a8171')
  stepMat.metallic = 0
  stepMat.roughness = 0.75;
  [0, 1, 2].forEach((i) => {
    const w = DOOR_W + 2.6 - i * 0.6
    const d = 0.42
    const s = track(box(scene, 'step' + i, w, 0.16, d, 0, -0.08 - i * 0.16, 0.4 + i * d, stepMat))
    s.receiveShadows = true
  })

  const signTex = track(new BABYLON.DynamicTexture('signTex', { width: 512, height: 128 }, scene, false))
  const signCtx = signTex.getContext() as unknown as CanvasRenderingContext2D
  signCtx.clearRect(0, 0, 512, 128)
  signCtx.fillStyle = '#fff6e6'
  signCtx.font = '800 48px Inter, sans-serif'
  signCtx.textAlign = 'center'
  signCtx.textBaseline = 'middle'
  signCtx.shadowColor = '#ffcf7d'
  signCtx.shadowBlur = 8
  signCtx.fillText('EGOWALL', 256, 66)
  signTex.update(false)
  signTex.vScale = -1
  signTex.vOffset = 1
  const signMat = track(new BABYLON.StandardMaterial('signMat', scene))
  signMat.emissiveTexture = signTex
  signMat.opacityTexture = signTex
  signMat.diffuseColor = BABYLON.Color3.Black()
  signMat.specularColor = BABYLON.Color3.Black()
  signMat.disableLighting = true
  signMat.backFaceCulling = false
  const sign = track(BABYLON.MeshBuilder.CreatePlane('sign', { width: 3, height: 0.75 }, scene))
  sign.position.set(0, DOOR_H + 0.5, -WALL_T - 0.02)
  sign.material = signMat
  sign.isPickable = false

  facadeCasters.forEach((m) => sunShadows.addShadowCaster(m, false))

  /* ---------- glass double doors ---------- */
  const glassMat = track(new BABYLON.PBRMaterial('glassMat', scene))
  glassMat.albedoColor = new BABYLON.Color3(0.86, 0.9, 0.93)
  glassMat.metallic = 0
  glassMat.roughness = 0.04
  glassMat.alpha = 0.16
  glassMat.environmentIntensity = 1.3
  glassMat.backFaceCulling = false

  const frameMat = track(new BABYLON.PBRMaterial('doorFrameMat', scene))
  frameMat.albedoColor = BABYLON.Color3.FromHexString('#141317')
  frameMat.metallic = 0.75
  frameMat.roughness = 0.35

  const doorHinges: { hinge: BABYLON.TransformNode; openAngle: number }[] = [];
  [-1, 1].forEach((side) => {
    const hinge = track(new BABYLON.TransformNode('doorHinge' + side, scene))
    hinge.position.set((side * DOOR_W) / 2, 0, -WALL_T / 2)
    const inward = -side
    const leafW = DOOR_W / 2 - 0.04
    const pane = track(box(scene, 'doorGlass' + side, leafW, DOOR_H, 0.04, (inward * leafW) / 2, DOOR_H / 2, 0, glassMat))
    pane.parent = hinge
    const bar = track(box(scene, 'doorBar' + side, leafW, 0.08, 0.06, (inward * leafW) / 2, DOOR_H * 0.5, 0, frameMat))
    bar.parent = hinge
    const edge = track(box(scene, 'doorEdge' + side, 0.06, DOOR_H, 0.06, inward * (leafW - 0.02), DOOR_H / 2, 0, frameMat))
    edge.parent = hinge
    doorHinges.push({ hinge, openAngle: side * 1.92 })
  })

  /* ---------- interior hall: mirror floor, walls, skylight, dust ---------- */
  const hallFloorMat = track(new BABYLON.StandardMaterial('hallFloorMat', scene))
  hallFloorMat.diffuseColor = BABYLON.Color3.FromHexString('#100e14')
  hallFloorMat.specularColor = BABYLON.Color3.FromHexString('#3a3542')
  hallFloorMat.specularPower = 64
  const hallFloor = track(BABYLON.MeshBuilder.CreateGround('hallFloor', { width: W_IN, height: D_IN }, scene))
  hallFloor.position.z = D_IN / 2
  hallFloor.material = hallFloorMat
  hallFloor.receiveShadows = true

  const mirror = track(new BABYLON.MirrorTexture('floorMirror', 1024, scene, true))
  mirror.mirrorPlane = new BABYLON.Plane(0, -1, 0, 0)
  mirror.level = 0.4
  mirror.adaptiveBlurKernel = 6
  mirror.renderList = []
  hallFloorMat.reflectionTexture = mirror

  const wallMat = track(new BABYLON.PBRMaterial('hallWallMat', scene))
  wallMat.albedoColor = BABYLON.Color3.FromHexString('#3a3540')
  wallMat.metallic = 0
  wallMat.roughness = 0.92
  wallMat.environmentIntensity = 0.5
  wallMat.backFaceCulling = false

  const endWallMat = track(new BABYLON.PBRMaterial('endWallMat', scene))
  endWallMat.albedoColor = BABYLON.Color3.FromHexString('#241a24')
  endWallMat.metallic = 0
  endWallMat.roughness = 0.85
  endWallMat.environmentIntensity = 0.5
  endWallMat.backFaceCulling = false

  const ceilMat = track(new BABYLON.PBRMaterial('hallCeilMat', scene))
  ceilMat.albedoColor = BABYLON.Color3.FromHexString('#0d0b12')
  ceilMat.metallic = 0
  ceilMat.roughness = 0.95
  ceilMat.backFaceCulling = false

  track(wallPlane(scene, 'hallWallL', D_IN, H_IN, -W_IN / 2, H_IN / 2, D_IN / 2, Math.PI / 2, wallMat))
  track(wallPlane(scene, 'hallWallR', D_IN, H_IN, W_IN / 2, H_IN / 2, D_IN / 2, -Math.PI / 2, wallMat))
  const hallWallEnd = track(wallPlane(scene, 'hallWallEnd', W_IN, H_IN, 0, H_IN / 2, D_IN, Math.PI, endWallMat))
  const ceil = track(wallPlane(scene, 'hallCeil', W_IN, D_IN, 0, H_IN, D_IN / 2, 0, ceilMat))
  ceil.rotation.set(Math.PI / 2, 0, 0)
  ceil.position.set(0, H_IN, D_IN / 2)

  const skyStripMat = track(new BABYLON.StandardMaterial('skyStripMat', scene))
  skyStripMat.emissiveColor = BABYLON.Color3.FromHexString('#cdd8e8')
  skyStripMat.disableLighting = true
  const skyStrip = track(BABYLON.MeshBuilder.CreatePlane('skyStrip', { width: 1.5, height: D_IN - 3 }, scene))
  skyStrip.rotation.x = Math.PI / 2
  skyStrip.position.set(0, H_IN - 0.04, D_IN / 2)
  skyStrip.material = skyStripMat
  skyStrip.isPickable = false
  const skylightGlow = track(new BABYLON.PointLight('skylightGlow', new BABYLON.Vector3(0, H_IN - 0.5, D_IN / 2), scene))
  skylightGlow.diffuse = BABYLON.Color3.FromHexString('#c7d3e6')
  skylightGlow.intensity = 0.5
  skylightGlow.range = 30

  const shaftCoreMat = track(new BABYLON.StandardMaterial('shaftCoreMat', scene))
  shaftCoreMat.emissiveColor = BABYLON.Color3.FromHexString('#eef1f8')
  shaftCoreMat.alpha = 0.055
  shaftCoreMat.disableLighting = true
  shaftCoreMat.backFaceCulling = false
  const shaftHazeMat = track(new BABYLON.StandardMaterial('shaftHazeMat', scene))
  shaftHazeMat.emissiveColor = BABYLON.Color3.FromHexString('#dfe6f2')
  shaftHazeMat.alpha = 0.022
  shaftHazeMat.disableLighting = true
  shaftHazeMat.backFaceCulling = false;
  [4, 12.5, 21].forEach((z) => {
    const core = track(BABYLON.MeshBuilder.CreateCylinder('shaftCore', { diameterTop: 0.5, diameterBottom: 1.7, height: H_IN - 0.2, tessellation: 18 }, scene))
    core.position.set(0, H_IN / 2, z)
    core.material = shaftCoreMat
    core.isPickable = false
    const haze = track(BABYLON.MeshBuilder.CreateCylinder('shaftHaze', { diameterTop: 1.3, diameterBottom: 4.2, height: H_IN - 0.2, tessellation: 18 }, scene))
    haze.position.set(0, H_IN / 2, z)
    haze.material = shaftHazeMat
    haze.isPickable = false
  })

  const dust = track(new BABYLON.ParticleSystem('dust', 260, scene))
  dust.particleTexture = track(new BABYLON.Texture('https://playground.babylonjs.com/textures/flare.png', scene))
  dust.emitter = new BABYLON.Vector3(0, H_IN - 1, D_IN / 2)
  dust.minEmitBox = new BABYLON.Vector3(-4.5, 0, -11)
  dust.maxEmitBox = new BABYLON.Vector3(4.5, 1.2, 11)
  dust.color1 = new BABYLON.Color4(0.95, 0.94, 0.9, 0.16)
  dust.color2 = new BABYLON.Color4(0.9, 0.88, 0.82, 0.05)
  dust.minSize = 0.01
  dust.maxSize = 0.035
  dust.minLifeTime = 6
  dust.maxLifeTime = 11
  dust.emitRate = 14
  dust.direction1 = new BABYLON.Vector3(-0.03, -0.1, -0.03)
  dust.direction2 = new BABYLON.Vector3(0.03, -0.03, 0.03)
  dust.gravity = new BABYLON.Vector3(0, -0.01, 0)
  dust.blendMode = BABYLON.ParticleSystem.BLENDMODE_ONEONE
  dust.start()

  /* ---------- museum track fixtures ---------- */
  const fixtureMat = track(new BABYLON.StandardMaterial('fixtureMat', scene))
  fixtureMat.diffuseColor = BABYLON.Color3.FromHexString('#1a1a1f')
  fixtureMat.specularColor = BABYLON.Color3.Black()
  const bulbMat = track(new BABYLON.StandardMaterial('bulbMat', scene))
  bulbMat.emissiveColor = BABYLON.Color3.FromHexString('#ffdba8').scale(0.55)
  bulbMat.disableLighting = true

  function trackFixture(x: number, z: number, aimX: number, aimZ: number, warm: boolean, intensity: number) {
    const head = track(BABYLON.MeshBuilder.CreateCylinder('fx', { diameter: 0.16, height: 0.22, tessellation: 10 }, scene))
    head.position.set(x, H_IN - 0.14, z)
    head.material = fixtureMat
    head.isPickable = false
    const tip = track(BABYLON.MeshBuilder.CreateSphere('fxTip', { diameter: 0.09 }, scene))
    tip.position.set(x, H_IN - 0.24, z)
    tip.material = bulbMat
    tip.isPickable = false

    const spot = track(new BABYLON.SpotLight('spot', new BABYLON.Vector3(x, H_IN - 0.25, z), BABYLON.Vector3.Zero(), Math.PI / 4.2, 16, scene))
    const target = new BABYLON.Vector3(aimX, 0.4, aimZ)
    spot.direction = target.subtract(spot.position).normalize()
    spot.diffuse = warm ? BABYLON.Color3.FromHexString('#ffdba8') : BABYLON.Color3.FromHexString('#eef1ff')
    spot.intensity = intensity
    spot.range = 10
  }

  /* ---------- framed prints — box frame + box mat + emissive photo plane ---------- */
  const frameWoodMat = track(new BABYLON.PBRMaterial('frameWoodMat', scene))
  frameWoodMat.albedoColor = BABYLON.Color3.FromHexString('#0e0c10')
  frameWoodMat.metallic = 0.1
  frameWoodMat.roughness = 0.4
  const matBoardMat = track(new BABYLON.StandardMaterial('matBoardMat', scene))
  matBoardMat.diffuseColor = BABYLON.Color3.FromHexString('#efe9dd')
  matBoardMat.specularColor = BABYLON.Color3.Black()

  const texCache = new Map<string, BABYLON.Texture>()
  function texFor(url: string): BABYLON.Texture {
    let tex = texCache.get(url)
    if (!tex) {
      tex = new BABYLON.Texture(url, scene)
      tex.anisotropicFilteringLevel = 8
      texCache.set(url, tex)
      disposers.push(() => tex!.dispose())
    }
    return tex
  }

  function hangPrint(photo: HallPrintPhoto, w: number, x: number, z: number, rotY: number, y: number, matW = 0.07, borderD = 0.09) {
    const h = w / photo.ratio
    const photoMat = track(new BABYLON.StandardMaterial('photoMat', scene))
    const tex = texFor(photo.url)
    photoMat.diffuseTexture = tex
    photoMat.emissiveTexture = tex
    photoMat.emissiveColor = BABYLON.Color3.FromHexString('#4a4a4a')
    photoMat.specularColor = BABYLON.Color3.FromHexString('#161616')

    const frame = track(BABYLON.MeshBuilder.CreateBox('printFrame', { width: w + 2 * (matW + borderD), height: h + 2 * (matW + borderD), depth: 0.09 }, scene))
    frame.material = frameWoodMat
    const mat = track(BABYLON.MeshBuilder.CreateBox('printMat', { width: w + 2 * matW, height: h + 2 * matW, depth: 0.02 }, scene))
    mat.material = matBoardMat
    const photoMesh = track(BABYLON.MeshBuilder.CreatePlane('printPhoto', { width: w, height: h }, scene))
    photoMesh.material = photoMat

    const dir = new BABYLON.Vector3(Math.sin(rotY), 0, Math.cos(rotY))
    frame.position.set(x, y, z).addInPlace(dir.scale(0.045))
    mat.position.set(x, y, z).addInPlace(dir.scale(0.09));
    photoMesh.position.set(x, y, z).addInPlace(dir.scale(0.11));
    [frame, mat].forEach((m) => {
      m.rotation.y = rotY
    })
    // A plane's front-face yaw convention is opposite a box's — using rotY
    // directly here would face every photo INTO its wall instead of out.
    photoMesh.rotation.y = rotY === Math.PI ? 0 : -rotY;
    [frame, mat, photoMesh].forEach((m) => {
      m.receiveShadows = true
    })
    mirror.renderList?.push(frame, mat, photoMesh)
    return { frame, mat, photo: photoMesh }
  }

  // Cycle real photos to fill the hall's fixed 7 slots (6 side-wall + 1
  // finale) — see this module's doc comment.
  const slotPhotos: HallPrintPhoto[] = []
  for (let i = 0; i < 7; i++) slotPhotos.push(photos[i % photos.length])

  const zRows = [7, 13, 19];
  [-1, 1].forEach((side, sideIdx) => {
    zRows.forEach((z, i) => {
      const photo = slotPhotos[sideIdx * 3 + i]
      const w = i === 2 ? 1.9 : i === 1 ? 1.35 : 1.5
      const x = side * (W_IN / 2 - 0.16)
      const rotY = side === -1 ? Math.PI / 2 : -Math.PI / 2
      hangPrint(photo, w, x, z, rotY, 2.0)
      const fixX = side * (W_IN / 2 - 1.5)
      trackFixture(fixX, z - 0.6, x, z, true, 11)
    })
  })

  const finalePhoto = slotPhotos[6]
  hangPrint(finalePhoto, 3.1, 0, D_IN - 0.14, Math.PI, 2.15, 0.09, 0.16)
  trackFixture(-1.1, D_IN - 3.4, 0, D_IN, true, 12)
  trackFixture(1.1, D_IN - 3.4, 0, D_IN, true, 12)

  /* ---------- centerpiece sculpture ---------- */
  const sculptMat = track(new BABYLON.PBRMaterial('sculptMat', scene))
  sculptMat.albedoColor = BABYLON.Color3.FromHexString('#caa15a')
  sculptMat.metallic = 1
  sculptMat.roughness = 0.22
  sculptMat.environmentIntensity = 1.2
  const sculpt = track(BABYLON.MeshBuilder.CreateTorusKnot('sculpt', { radius: 0.42, tube: 0.13, radialSegments: 128, tubularSegments: 24 }, scene))
  sculpt.position.set(0, 1.35, 13)
  sculpt.material = sculptMat
  sculpt.receiveShadows = true
  mirror.renderList?.push(sculpt)

  const plinthMat = track(new BABYLON.PBRMaterial('plinthMat', scene))
  plinthMat.albedoColor = BABYLON.Color3.FromHexString('#171319')
  plinthMat.metallic = 0
  plinthMat.roughness = 0.5
  const plinth = track(BABYLON.MeshBuilder.CreateBox('plinth', { width: 0.7, height: 0.85, depth: 0.7 }, scene))
  plinth.position.set(0, 0.425, 13)
  plinth.material = plinthMat
  plinth.receiveShadows = true
  mirror.renderList?.push(plinth)

  const sculptSpot = track(new BABYLON.SpotLight('sculptSpot', new BABYLON.Vector3(0, H_IN - 0.3, 13), new BABYLON.Vector3(0, -1, 0.02), Math.PI / 5, 12, scene))
  sculptSpot.diffuse = BABYLON.Color3.FromHexString('#fff2da')
  sculptSpot.intensity = 22
  sculptSpot.range = 9
  const sculptShadows = track(new BABYLON.ShadowGenerator(1024, sculptSpot))
  sculptShadows.useBlurExponentialShadowMap = true
  sculptShadows.blurKernel = 20
  sculptShadows.addShadowCaster(sculpt, false)
  sculptShadows.addShadowCaster(plinth, false)

  ;[scene.getMeshByName('hallWallL'), scene.getMeshByName('hallWallR'), hallWallEnd, ceil].forEach((m) => {
    if (m) mirror.renderList?.push(m)
  })

  /* ---------- camera + cinematic intro ---------- */
  const START_POS = new BABYLON.Vector3(0, 1.68, -15.5)
  const START_LOOK = new BABYLON.Vector3(0, 2.0, 2)
  const cam = track(new BABYLON.UniversalCamera('present-cam', START_POS.clone(), scene))
  cam.minZ = 0.05
  cam.fov = 0.82
  cam.speed = 0.16
  cam.angularSensibility = 3200
  cam.keysUp = [87, 38]
  cam.keysDown = [83, 40]
  cam.keysLeft = [65, 37]
  cam.keysRight = [68, 39]
  cam.setTarget(START_LOOK)
  scene.activeCamera = cam

  const ssao = track(new BABYLON.SSAO2RenderingPipeline('present-ssao', scene, { ssaoRatio: 0.75, blurRatio: 1 }, [cam]))
  ssao.radius = 2.2
  ssao.totalStrength = 1.05
  ssao.expensiveBlur = true
  ssao.samples = 12

  const pipe = track(new BABYLON.DefaultRenderingPipeline('present-pipe', true, scene, [cam]))
  pipe.fxaaEnabled = true
  pipe.bloomEnabled = true
  pipe.bloomThreshold = 0.82
  pipe.bloomWeight = 0.22
  pipe.bloomKernel = 32
  pipe.bloomScale = 0.5
  pipe.imageProcessingEnabled = true
  pipe.imageProcessing.toneMappingEnabled = true
  pipe.imageProcessing.toneMappingType = BABYLON.ImageProcessingConfiguration.TONEMAPPING_ACES
  pipe.imageProcessing.exposure = 1.05
  pipe.imageProcessing.contrast = 1.06
  pipe.imageProcessing.vignetteEnabled = true
  pipe.imageProcessing.vignetteWeight = 1.6
  pipe.imageProcessing.vignetteColor = new BABYLON.Color4(0.01, 0.01, 0.03, 0)

  let goal: { from: BABYLON.Vector3; fromLook: BABYLON.Vector3; to: BABYLON.Vector3; toLook: BABYLON.Vector3; t: number; ms: number; onDone?: () => void } | null = null
  function camLookTarget() {
    return cam.position.add(cam.getDirection(BABYLON.Axis.Z).scale(6))
  }
  function tweenTo(pos: BABYLON.Vector3, look: BABYLON.Vector3, ms: number, onDone?: () => void) {
    goal = { from: cam.position.clone(), fromLook: camLookTarget(), to: pos.clone(), toLook: look.clone(), t: 0, ms, onDone }
  }
  const tweenObserver = scene.onBeforeRenderObservable.add(() => {
    if (!goal) return
    goal.t += engine.getDeltaTime()
    const k = Math.min(1, goal.t / goal.ms)
    const e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2
    cam.position = BABYLON.Vector3.Lerp(goal.from, goal.to, e)
    cam.setTarget(BABYLON.Vector3.Lerp(goal.fromLook, goal.toLook, e))
    if (k >= 1) {
      const done = goal.onDone
      goal = null
      if (done) done()
    }
  })
  disposers.push(() => scene.onBeforeRenderObservable.remove(tweenObserver))

  let doorAnimFrame: ReturnType<typeof requestAnimationFrame> | null = null
  function setDoors(open: boolean, ms = 1400) {
    doorHinges.forEach((d) => {
      const from = d.hinge.rotation.y
      const to = open ? d.openAngle : 0
      const t0 = performance.now()
      const step = () => {
        const k = Math.min(1, (performance.now() - t0) / ms)
        const e = 1 - Math.pow(1 - k, 3)
        d.hinge.rotation.y = from + (to - from) * e
        if (k < 1) doorAnimFrame = requestAnimationFrame(step)
      }
      step()
    })
  }
  disposers.push(() => {
    if (doorAnimFrame != null) cancelAnimationFrame(doorAnimFrame)
  })

  let insideMode = false
  function enableExplore() {
    insideMode = true
    cam.attachControl(canvas, true)
    callbacks.onExploreChange?.(true)
  }
  function playIntro(skip = false) {
    if (skip) {
      doorHinges.forEach((d) => {
        d.hinge.rotation.y = d.openAngle
      })
      cam.position = new BABYLON.Vector3(0, 1.72, 5)
      cam.setTarget(new BABYLON.Vector3(0, 1.9, 13))
      enableExplore()
      return
    }
    setTimeout(() => setDoors(true, 1500), 900)
    tweenTo(new BABYLON.Vector3(0, 1.7, -4.6), new BABYLON.Vector3(0, 1.95, 6), 3400, () => {
      tweenTo(new BABYLON.Vector3(0, 1.72, 5), new BABYLON.Vector3(0, 1.92, 13), 2600, enableExplore)
    })
  }
  function stepOutside() {
    insideMode = false
    cam.detachControl()
    goal = null
    cam.position = START_POS.clone()
    cam.setTarget(START_LOOK)
    setDoors(false, 1100)
    callbacks.onExploreChange?.(false)
  }

  scene.executeWhenReady(() => callbacks.onReady?.())

  return {
    camera: cam,
    playIntro,
    stepOutside,
    isInside: () => insideMode,
    dispose: () => {
      disposers.reverse().forEach((d) => {
        try {
          d()
        } catch {
          // scene may already be tearing down (e.g. StrictMode double-effect) — ignore
        }
      })
    },
  }
}
